import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { PageHeader, SeverityDot, Empty } from "../components/ui-helpers";
import { ArrowRight, Check, Checks } from "@phosphor-icons/react";

// Resolve the in-app destination for a notification so clicking it takes the
// user straight to the task they need to act on (sign a document, complete a
// tracker task, review a ticket, etc.). Returns a route string or null.
function notifLink(n) {
  if (n.document_id) return `/documents/${n.document_id}`;      // signature / approval on a P&P document
  if (n.requirement_id) return `/requirements/${n.requirement_id}`;
  const rt = n.related_type || "";
  const rid = n.related_id;
  if (rt === "policy_manual") return "/manuals";
  if (rt === "flow") return "/claroflow";
  if (rt === "investigation" && rid) return `/investigations/${rid}`;
  if (rt === "tracker") return n.track === "op" ? "/opwatch" : "/cipwatch";
  if (rt.startsWith("cip004")) return "/cip004";
  const k = n.kind || "";
  if (k.startsWith("cip004")) return "/cip004";
  if (k === "tracker") return "/cipwatch";
  return null;
}

export default function Notifications() {
  const [items, setItems] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => api.get("/notifications").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const markRead = async (n) => {
    try { await api.patch(`/notifications/${n.id}/read`); window.dispatchEvent(new Event("notifications-updated")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const markAll = async () => {
    try { await api.post("/notifications/read-all"); toast.success("All marked complete"); window.dispatchEvent(new Event("notifications-updated")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Click a notification → mark it read (best-effort) and jump to the task.
  const openNotif = async (n) => {
    const link = notifLink(n);
    if (!n.read) {
      try { await api.patch(`/notifications/${n.id}/read`); window.dispatchEvent(new Event("notifications-updated")); }
      catch { /* non-blocking */ }
    }
    if (link) nav(link);
    else { toast.info("This notification has no linked task."); load(); }
  };

  if (!items) return <div className="p-8 font-mono text-sm">Loading…</div>;
  const unread = items.filter((n) => !n.read).length;

  return (
    <div>
      <PageHeader title="Notifications" subtitle={`${unread} item(s) awaiting your action`} />
      <div className="p-4 sm:p-8 space-y-3 max-w-3xl">
        <div className="flex justify-end">
          {items.length > 0 && (
            <button data-testid="mark-all-read-btn" onClick={markAll}
              className="flex items-center gap-1 border border-border px-3 py-1.5 text-sm font-semibold hover:bg-secondary">
              <Checks size={15} /> Mark all complete
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <Empty title="No notifications" hint="Run “Notify owners” from Missing Evidence to dispatch prompts." />
        ) : items.map((n) => {
          const link = notifLink(n);
          return (
            <div key={n.id} data-testid={`notif-${n.id}`}
              onClick={link ? () => openNotif(n) : undefined}
              className={`border bg-card px-5 py-3 flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4 transition-colors ${link ? "cursor-pointer hover:border-primary" : ""} ${n.read ? "border-border opacity-60" : "border-l-4 border-l-red-500 border-border"}`}>
              <div className="flex items-start gap-3 min-w-0">
                <SeverityDot severity={n.severity} />
                <div className="min-w-0 break-words">
                  <div className="font-semibold text-sm flex items-center gap-2">
                    {n.title}
                    {n.role_label && (
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${n.kind === "verify" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>{n.role_label}</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
                  <div className="text-xs text-muted-foreground font-mono mt-1">{n.owner} · {n.created_at?.slice(0, 10)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                {link && (
                  <button data-testid={`notif-open-${n.id}`} onClick={() => openNotif(n)}
                    className="flex items-center gap-1 text-sm font-semibold hover:text-blue-600">Open <ArrowRight size={14} /></button>
                )}
                {!n.read && (
                  <button data-testid={`notif-read-${n.id}`} onClick={() => markRead(n)}
                    className="flex items-center gap-1 border border-border px-2 py-1 text-xs font-semibold hover:bg-primary hover:text-white"><Check size={13} /> Mark complete</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
